import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBusiness } from '../context/BusinessContext';
import { LogOut } from 'lucide-react';
import MetricsTabs from '../components/AgentsDashboard/MetricsTabs';

// Mock agents data
const MOCK_AGENTS = [
  {
    id: 'demand-agent',
    name: 'Demand Agent',
    icon: '📊',
    description: 'Forecasts demand and optimizes inventory levels',
    status: 'active',
    whatsappConnected: true,
    lastMessage: 'Forecast updated for next week',
    lastMessageTime: '30 min ago',
    unreadCount: 0,
    messages: [
      {
        sender: 'agent',
        content: 'Good morning! I\'ve processed today\'s POS data across all 6 branches. Forecast for next week is ready.',
        timestamp: '9:15 AM',
        channel: 'dashboard',
      },
      {
        sender: 'user',
        content: 'What\'s the demand trend for fresh produce?',
        timestamp: '9:16 AM',
      },
      {
        sender: 'agent',
        content: 'Fresh produce demand is up 8% week-over-week. Highest demand on weekends. Recommend increasing supplier orders by 10% for Fri-Sun window.',
        timestamp: '9:17 AM',
        channel: 'whatsapp',
      },
    ],
  },
  {
    id: 'ordering-agent',
    icon: '📦',
    name: 'Ordering Agent',
    description: 'Generates and manages purchase orders',
    status: 'waiting',
    whatsappConnected: true,
    lastMessage: 'Awaiting confirmation on 3 POs',
    lastMessageTime: '2h ago',
    unreadCount: 3,
    messages: [
      {
        sender: 'agent',
        content: 'Generated 12 POs based on forecast. 9 have been confirmed. Awaiting response from 3 suppliers.',
        timestamp: '7:30 AM',
        channel: 'dashboard',
      },
      {
        sender: 'agent',
        content: 'Re-sent confirmation requests to Supplier A, B, C via WhatsApp. ETA response: 4h',
        timestamp: '7:45 AM',
        channel: 'whatsapp',
      },
    ],
  },
  {
    id: 'claims-agent',
    icon: '🎯',
    name: 'Claims Agent',
    description: 'Files and tracks short-ship and billing disputes',
    status: 'active',
    whatsappConnected: true,
    lastMessage: '7 claims filed, $2,450 recovered',
    lastMessageTime: 'Just now',
    unreadCount: 0,
    messages: [
      {
        sender: 'agent',
        content: '📊 Claims Summary\n7 claims filed today, $2,450 recovered.\n3 awaiting supplier response.\nTop category: Produce ($1,200 of claims)',
        timestamp: 'Just now',
        channel: 'whatsapp',
      },
    ],
  },
  {
    id: 'receiving-agent',
    icon: '📥',
    name: 'Receiving Agent',
    description: 'Matches goods received against orders and invoices',
    status: 'active',
    whatsappConnected: false,
    lastMessage: 'All synced for today',
    lastMessageTime: '15 min ago',
    unreadCount: 0,
    messages: [
      {
        sender: 'agent',
        content: 'Good news! Today\'s receiving is 100% matched. All goods received vs PO vs invoice aligned. No discrepancies detected.',
        timestamp: '3:45 PM',
        channel: 'dashboard',
      },
    ],
  },
  {
    id: 'scoring-agent',
    icon: '⭐',
    name: 'Scoring Agent',
    description: 'Tracks supplier reliability and performance',
    status: 'active',
    whatsappConnected: true,
    lastMessage: 'Supplier reliability snapshot ready',
    lastMessageTime: '1h ago',
    unreadCount: 0,
    messages: [
      {
        sender: 'agent',
        content: 'Supplier reliability update:\n✓ Supplier A: 98% on-time (+2% vs last week)\n⚠ Supplier B: 78% on-time (-5%, recommend review)\n✓ New Supplier C: 100% on-time (5 orders)',
        timestamp: '2:30 PM',
        channel: 'whatsapp',
      },
    ],
  },
  {
    id: 'confirmation-agent',
    icon: '✓',
    name: 'Confirmation Agent',
    description: 'Chases PO confirmations and manages supplier communication',
    status: 'waiting',
    whatsappConnected: true,
    lastMessage: 'Chasing 3 suppliers for confirmation',
    lastMessageTime: '45 min ago',
    unreadCount: 0,
    messages: [
      {
        sender: 'agent',
        content: 'Sent confirmation requests for PO-2024-001, PO-2024-002, PO-2024-003 to Supplier A, B, C.',
        timestamp: '10:30 AM',
        channel: 'dashboard',
      },
      {
        sender: 'agent',
        content: 'Re-sending via WhatsApp due to no response. Setting 4h timeout.',
        timestamp: '10:45 AM',
        channel: 'whatsapp',
      },
    ],
  },
];

export default function AgentsDashboard() {
  const { user, loading, logout } = useAuth();
  const { currentBusiness, loading: businessLoading } = useBusiness();
  const navigate = useNavigate();
  const [selectedAgent, setSelectedAgent] = useState('demand-agent');
  const [agents, setAgents] = useState(MOCK_AGENTS);

  useEffect(() => {
    if (loading || businessLoading) return;
    if (!user) navigate('/login', { replace: true });
  }, [user, loading, businessLoading, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSelectAgent = (agentId) => {
    setSelectedAgent(agentId);
  };

  const handleSendMessage = (message) => {
    setAgents(
      agents.map((agent) => {
        if (agent.id === selectedAgent) {
          const newMessage = {
            sender: 'user',
            content: message,
            timestamp: new Date().toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }),
          };
          return {
            ...agent,
            messages: [...(agent.messages || []), newMessage],
            lastMessage: message.substring(0, 50) + (message.length > 50 ? '...' : ''),
            lastMessageTime: 'Just now',
          };
        }
        return agent;
      })
    );

    // Simulate agent response
    setTimeout(() => {
      setAgents(
        agents.map((agent) => {
          if (agent.id === selectedAgent) {
            const agentResponses = {
              'demand-agent':
                "I'll analyze that for you. Based on current trends, here's my recommendation...",
              'ordering-agent': 'I can adjust the order quantities. Should I prioritize faster suppliers?',
              'claims-agent': 'I\'ll track this claim and follow up with the supplier tomorrow.',
              'receiving-agent':
                'Noted. I\'ll monitor for any discrepancies in tomorrow\'s receiving.',
              'scoring-agent': 'This supplier will move to the priority tier after this update.',
              'confirmation-agent': 'I\'ll escalate this to the category manager for approval.',
            };

            const agentResponse = {
              sender: 'agent',
              content:
                agentResponses[selectedAgent] ||
                "I'm processing that request. Give me a moment...",
              timestamp: new Date().toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              }),
              channel: 'dashboard',
            };

            return {
              ...agent,
              messages: [...(agent.messages || []), agentResponse],
              lastMessage: agentResponse.content.substring(0, 50) + '...',
              lastMessageTime: 'Just now',
            };
          }
          return agent;
        })
      );
    }, 1500);
  };

  if (loading || businessLoading || !user) {
    return (
      <div className="min-h-screen bg-[#050506] text-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-[#2E6BFF] rounded-full animate-spin" />
      </div>
    );
  }

  const currentAgentData = agents.find((a) => a.id === selectedAgent) || agents[0];

  return (
    <div className="h-screen flex flex-col bg-[#050506] text-white">
      {/* Top Navigation */}
      <header className="border-b border-white/5 bg-[#050506]/85 backdrop-blur sticky top-0 z-30 h-16">
        <div className="max-w-full px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-lg font-semibold">🤖 Agent Dashboard</h1>
            {currentBusiness && (
              <div className="hidden md:flex items-center gap-2 text-white/70 text-[13px] border-l border-white/10 pl-4">
                <span className="text-green-400">📍</span>
                <p className="font-medium text-white">{currentBusiness.name}</p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-white/10 grid place-items-center text-[11px]">
                {user.name?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
            <button
              onClick={handleLogout}
              className="btn-ghost text-[12px] flex items-center gap-1"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content: Metrics Tabs */}
      <div className="flex-1 overflow-hidden">
        <MetricsTabs
          agents={agents}
          selectedAgent={selectedAgent}
          onSelectAgent={handleSelectAgent}
          onSendMessage={handleSendMessage}
        />
      </div>
    </div>
  );
}
